import React, { useEffect, useRef, useState } from 'react';
import { ReadyState } from 'react-use-websocket';
import { faWifiSlash } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { COPILOT_CHAT_SETTINGS, PLATFORM_API_URL } from '@constants';
import { ChatSettings, useCopilotWebSocket } from '@hooks';
import { localStorage } from '@utils';

import { Button, ChaseSpinner, Icon, Render } from '@components';
import { useChat } from '@components/Providers';

import { ChatBubbleLoader } from './ChatBubbleLoader';
import { ChatFooter } from './ChatFooter';
import { ChatHeader } from './ChatHeader';
import { ChatMessage } from './ChatMessage';
import { ChatSuggestions } from './ChatSuggestions';

const socketApiUrl = PLATFORM_API_URL.replace('https', 'wss');
const WEB_SOCKET_URL = `${socketApiUrl}/apis/copilot/ws`;

const copilotChatSettings = localStorage.get<ChatSettings>(
  COPILOT_CHAT_SETTINGS,
) ?? { mode: 'hybrid', maxIterations: 3 };

export const ChatSidebar: React.FC = () => {
  const { isOpen, closeChat, messageStatus, setMessageStatus } = useChat();

  const messagesScrollRef = useRef<HTMLDivElement>(null);

  const [messageValue, setMessageValue] = useState('');

  const [error, setError] = useState<string | null>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isShowScrollButton, setIsShowScrollButton] = useState(false);
  const [chatSettings, setChatSettings] =
    useState<ChatSettings>(copilotChatSettings);

  const {
    messages,
    readyState,
    isWaitingResponse,
    reconnect,
    sendMessage,
    clearHistory,
    resendMessage,
  } = useCopilotWebSocket({
    url: WEB_SOCKET_URL,
    shouldConnect: isOpen,
    settings: chatSettings,
  });

  const isDisabledSendButton =
    !isOpen ||
    readyState !== ReadyState.OPEN ||
    messageValue.trim().length === 0 ||
    isWaitingResponse;

  const clearError = () => {
    setError(null);
  };

  const handleChatSettingsChange = (settings: ChatSettings) => {
    setChatSettings(settings);
    localStorage.set(COPILOT_CHAT_SETTINGS, settings);
  };

  const scrollToBottom = () => {
    messagesScrollRef.current?.scrollTo(0, 0);
  };

  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  const handleCloseChat = () => {
    setIsFullscreen(false);
    closeChat();
  };

  const onSendMessage = async (message: string) => {
    try {
      clearError();
      sendMessage(message);
      scrollToBottom();
    } catch {
      setMessageValue(message);
      setError('Failed to send message');
    }
  };

  const handleMessage = (e: React.FormEvent) => {
    e.preventDefault();

    const value = messageValue.trim();

    if (value.length === 0 || isDisabledSendButton) {
      return;
    }

    setMessageValue('');
    onSendMessage(messageValue.trim().replace(/\n/g, '  \n'));
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSendMessage(suggestion);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;

    if (target.scrollTop < -300) {
      setIsShowScrollButton(true);
    } else {
      setIsShowScrollButton(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isOpen && isFullscreen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    const onKey = (e: KeyboardEvent) => {
      // Esc — exit, Ctrl/Cmd+Shift+F — toggle full screen mode
      const isToggle =
        e.shiftKey &&
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'F' || e.key === 'f');

      if (isToggle) {
        e.preventDefault();
        setIsFullscreen((v) => !v);
      }

      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', onKey);

    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, isFullscreen]);

  useEffect(() => {
    if (isWaitingResponse) {
      setMessageStatus('loading');
      return;
    }

    if (!isOpen && messageStatus === 'loading') {
      setMessageStatus('loaded');
      return;
    }

    setMessageStatus('empty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isWaitingResponse]);

  const isEmptyMessages = messages.length === 0;

  return (
    <div
      className={clsx(
        'fixed right-0 top-0 z-30 flex h-full flex-col bg-white transition-transform duration-300 ease-in-out',
        {
          'translate-x-0': isOpen || isFullscreen,
          'translate-x-full': !isOpen && !isFullscreen,
          'w-[380px]': !isFullscreen,
          'inset-0 w-full translate-x-0': isFullscreen,
        },
      )}
      data-fullscreen={isFullscreen ? 'true' : 'false'}
    >
      <ChatHeader
        messages={messages}
        readyState={readyState}
        isFullscreen={isFullscreen}
        chatSettings={chatSettings}
        isWaitingResponse={isWaitingResponse}
        onCloseChat={handleCloseChat}
        onClearHistory={clearHistory}
        toggleFullscreen={toggleFullscreen}
        onSettingsChange={handleChatSettingsChange}
      />

      <div
        className={clsx('m-auto flex w-full flex-col overflow-hidden', {
          'flex-1': !isEmptyMessages || !isFullscreen,
          'flex-0': isEmptyMessages && isFullscreen,
        })}
      >
        <div
          ref={messagesScrollRef}
          onScroll={handleScroll}
          className={clsx(
            'flex min-h-[240px] flex-1 flex-col-reverse items-center overflow-y-auto overflow-x-hidden scroll-smooth',
            {
              'opacity-0': !isOpen && !isFullscreen,
            },
          )}
        >
          <Render if={isEmptyMessages && readyState === ReadyState.CLOSED}>
            <div className="flex h-full w-full items-center justify-center pt-5 text-neural-01 duration-300 animate-in">
              <Icon
                size="6x"
                icon={faWifiSlash}
                className="h-[120px] w-[120px]"
              />
            </div>
          </Render>

          <Render if={isEmptyMessages && readyState === ReadyState.CONNECTING}>
            <div className="flex h-full w-full items-center justify-center pt-5 text-neural-01 duration-300 animate-in">
              <ChaseSpinner color="primary" className="h-12 w-12" />
            </div>
          </Render>

          <ul className="flex w-full max-w-[768px] flex-1 flex-col justify-end px-6 pb-5">
            <Render if={isEmptyMessages && readyState === ReadyState.OPEN}>
              <ChatSuggestions
                key={String(isOpen)}
                readyState={readyState}
                onSuggestion={handleSuggestionClick}
              />
            </Render>

            {messages.map((message, index) => (
              <ChatMessage
                key={message.id}
                message={message}
                readyState={readyState}
                isLastMessage={index === messages.length - 1}
                onResendMessage={resendMessage}
              />
            ))}

            <Render if={isWaitingResponse}>
              <ChatBubbleLoader />
            </Render>
          </ul>
        </div>

        <div className="relative z-10">
          <Button
            theme
            aria-label="Scroll to bottom"
            className={clsx(
              'absolute -top-16 left-1/2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-all',
              {
                'pointer-events-none opacity-0 translate-y-4':
                  !isShowScrollButton,
                'opacity-60 translate-y-0 hover:opacity-100':
                  isShowScrollButton,
              },
            )}
            onClick={scrollToBottom}
          >
            <Icon icon={faChevronDown} size="lg" className="text-neural-05" />
          </Button>

          <ChatFooter
            value={messageValue}
            readyState={readyState}
            isFullscreen={isFullscreen}
            isWaitingResponse={isWaitingResponse}
            error={isOpen && error ? error : null}
            isDisabledSendButton={isDisabledSendButton}
            reconnect={reconnect}
            clearError={clearError}
            onMessage={handleMessage}
            onValueChange={setMessageValue}
          />
        </div>
      </div>
    </div>
  );
};
